import React from 'react';
import { Location } from 'react-router-dom';

import {
  isDealerMember,
  isLHPHMember,
  isOrgAdmin,
  isSuperAdmin,
  canAccessUsersMenu,
  canRunStaffActions,
} from './utils';
import { User } from './types/User.type';
import { DealerCompanyFlags, anyFlagOn, dealerImports } from './utils/dealerCompanyFlags';
import { guestMenu, isGuest } from './utils/guestScreens';

const PowerBIIcon = (
  <svg viewBox="0 0 18 18" width="1em" height="1em" fill="currentColor" aria-hidden="true">
    <rect x="1" y="9" width="3" height="8" rx="0.5" />
    <rect x="5.5" y="5" width="3" height="12" rx="0.5" />
    <rect x="10" y="1" width="3" height="16" rx="0.5" />
    <rect x="14.5" y="7" width="3" height="10" rx="0.5" />
  </svg>
);

export const routesConfig = {
  path: '/',
  routes: [
    {
      path: '/home',
      name: 'Home',
      routes: [
        {
          name: 'Loan to Lease Matchup',
          path: '/home/loan-to-lease-matchup',
        },
      ],
    },
    {
      name: 'Dealers',
      path: '/dealers',
      'data-test': 'dealers',
      routes: [
        {
          name: 'Dealer Details',
          path: '/dealers/:id',
          hideInMenu: true,
          routes: [
            {
              name: 'Details',
              path: '/dealers/:id/details',
            },
            {
              name: 'Map',
              path: '/dealers/:id/map',
            },
          ],
        },
      ],
    },
    {
      name: 'Settings',
      path: '/settings',
      routesConfig: [
        {
          name: 'Dashboard',
          path: '/settings/dashboard',
        },
      ],
    },
    {
      name: 'Support',
      path: '/support',
      routesConfig: [
        {
          name: 'Dashboard',
          path: '/support/dashboard',
        },
      ],
    },
    {
      name: 'Video Training',
      path: '/video-training',
      routesConfig: [
        {
          name: 'Dashboard',
          path: '/video-training/dashboard',
        },
      ],
    },
    {
      name: 'Logout',
      path: '/logout',
    },
  ],
};

const getLoansRoutes = (user: User, id: string) => {
  if (isDealerMember(user)) {
    if (!id || id === ':id') {
      return {
        name: 'Loans',
        path: '/loans',
        routes: [],
      };
    }

    return {
      name: 'Loans',
      path: '/loans',
      routes: [
        {
          name: 'Dashboard',
          path: `/loans/${id}/dashboard`,
        },
        {
          name: 'Loan to Lease Matchup',
          path: `/loans/${id}/loan-to-lease-matchup`,
        },
        {
          name: 'Matchup Exceptions',
          path: `/loans/${id}/matchup-exceptions`,
        },
        // No Loan Match Status: legacy's dealer menu has no VIN lookup (Kevin, 2026-10-22,
        // lhphcapital-42bw).
        {
          name: 'Pending Funding',
          path: `/loans/${id}/pending-funding`,
        },
        {
          name: 'Pending Repo Swaps',
          path: `/loans/${id}/pending-repo-swaps`,
        },
        {
          name: 'Active Loans',
          path: `/loans/${id}/active-loans`,
        },

        {
          name: 'Bulk Payoff Requests',
          path: `/loans/${id}/bulk-payoff-requests`,
        },
        {
          name: 'Payoff Requests List',
          path: `/loans/${id}/payoff-requests-list`,
        },
        {
          name: 'Paid Off Loans',
          path: `/loans/${id}/paid-off-loans`,
        },
        {
          name: 'Funding Log',
          path: `/loans/${id}/funding-log`,
        },
        {
          name: 'Billing/Payment Log',
          path: `/loans/${id}/billing-payment-log`,
        },
        {
          name: 'Payoff Calculator',
          path: `/loans/${id}/payoff-calculator`,
        },
        {
          name: 'Loan Calculator',
          path: `/loans/${id}/loan-calculator`,
        },
      ],
    };
  }

  if (!id || id === ':id') {
    return {
      name: 'Loans',
      path: '/loans',
      routes: [],
    };
  }

  return {
    name: 'Loans',
    path: '/loans',
    'data-test': '/loans',
    routes: [
      {
        name: 'Dashboard',
        path: `/loans/${id}/dashboard`,
      },
      {
        name: 'Loan to Lease Matchup',
        path: `/loans/${id}/loan-to-lease-matchup`,
      },
      {
        name: 'Matchup Exceptions',
        path: `/loans/${id}/matchup-exceptions`,
      },
      {
        name: 'Loan Match Status',
        path: `/loans/${id}/loan-match-status`,
      },
      {
        name: 'Pending Funding',
        path: `/loans/${id}/pending-funding`,
      },
      {
        name: 'Pending Queue (All)',
        path: `/loans/${id}/pending-queue`,
      },
      {
        name: 'DocuSign Status',
        path: `/loans/${id}/docusign-status`,
      },
      {
        name: 'Exceptions Maintenance',
        path: `/loans/${id}/exceptions-maintenance`,
      },
      {
        name: 'Pending Repo Swaps',
        path: `/loans/${id}/pending-repo-swaps`,
      },
      {
        name: 'Active Loans',
        path: `/loans/${id}/active-loans`,
      },
      {
        name: 'Loan Plan Payoff Requests',
        path: `/loans/${id}/loan-plan-payoff-requests`,
      },
      {
        name: 'Close Full Term Loans',
        path: `/loans/${id}/close-full-term-loans`,
      },
      {
        name: 'Paid Off Loans',
        path: `/loans/${id}/paid-off-loans`,
      },
      {
        name: 'Funding Log',
        path: `/loans/${id}/funding-log`,
      },
      {
        name: 'Billing/Payment Log',
        path: `/loans/${id}/billing-payment-log`,
      },
      {
        name: 'Payoff Calculator',
        path: `/loans/${id}/payoff-calculator`,
      },
      {
        name: 'Loan Calculator',
        path: `/loans/${id}/loan-calculator`,
      },
      {
        name: 'Move Loans To Floorplan',
        path: `/loans/${id}/move-loans-to-floorplan`,
      },
    ],
  };
};

const getDealersRoutes = (user: User, id: string) => {
  // Legacy's dealer menu is Home, Loans, Reports, Imports, Video Training (Kevin's walkthrough,
  // 2026-10-22). Contacts and portal users are LHPH staff's to manage — Kevin, same day — and
  // the API refuses a dealer on them. (lhphcapital-42bw)
  if (isDealerMember(user)) {
    return null;
  }

  if (!id || id === ':id') {
    return {
      name: 'Dealers',
      path: '/dealers',
      'data-test': '/dealers',
      routes: [],
    };
  }
  return {
    name: 'Dealers',
    path: '/dealers',
    'data-test': '/dealers',
    routes: [
      {
        name: 'Dealership Details',
        path: `/dealers/${id}/details`,
      },
      {
        name: 'DMS & GPS Info',
        path: `/dealers/${id}/dms-gps-info`,
      },
      {
        name: 'Map',
        path: `/dealers/${id}/map`,
      },
      {
        name: 'Corporate Docs',
        path: `/dealers/${id}/corporate-docs`,
      },
      // Defaults is the rates-and-fees screen: LHPH staff only (lhphcapital-fdjb). The API
      // refuses a dealer login on the rate and fee lists it shows.
      ...(canRunStaffActions(user)
        ? [
            {
              name: 'Defaults',
              path: `/dealers/${id}/defaults`,
            },
          ]
        : []),
      {
        name: 'Risk',
        path: `/dealers/${id}/risk`,
      },
      {
        name: 'Contacts',
        path: `/dealers/${id}/contacts`,
      },
      {
        name: 'Portal Users',
        path: `/dealers/${id}/portal-users`,
      },
      {
        name: 'Unit Projections',
        path: `/dealers/${id}/unit-projections`,
      },
      {
        name: 'Dealer Borrowing Base',
        path: `/dealers/${id}/dealer-borrowing-base`,
      },
      // Billing Options is how — and whether — LHPH bills the dealer, plus the variable-rate
      // re-amortization: LHPH staff only (lhphcapital-dxj3).
      ...(canRunStaffActions(user)
        ? [
            {
              name: 'Billing Options',
              path: `/dealers/${id}/billing-options`,
            },
          ]
        : []),
    ],
  };
};

function getHomeRoutes(user: User) {
  if (isDealerMember(user)) {
    // No VIN Search: "they don't have the VIN lookup" (Kevin, 2026-10-22, lhphcapital-42bw).
    return [
      {
        name: 'News & Info',
        path: '/home/news-info',
      },
      {
        name: 'Lease Quote',
        path: '/home/lease-quote',
      },
    ];
  } else if (isLHPHMember(user) || isOrgAdmin(user)) {
    return [
      {
        name: 'News & Info',
        path: '/home/news-info',
      },
      {
        name: 'VIN Search',
        path: '/home/vin-search',
      },
      // Phone Directory disabled v0.63.8 — `phoneext` table is empty.
      // {
      //   name: 'Phone Directory',
      //   path: '/home/directory',
      // },
      {
        name: 'Calendar',
        path: '/home/calendar',
      },
      {
        name: 'Dashboards',
        path: '/home/dashboards',
      },
      {
        name: 'DocuSign Status',
        path: '/home/docusign-status',
      },
      {
        name: 'Lease Quote',
        path: '/home/lease-quote',
      },
      {
        name: 'Exports',
        path: '/home/exports',
      },
      {
        name: 'Borrowing Base Reports',
        path: '/home/borrowing-base-reports',
      },
      {
        name: 'Bulk Loanplan Billing',
        path: '/home/bulk-loanplan-billing',
      },
      {
        name: 'Prime Rate History',
        path: '/home/prime-rate-history',
      },
    ];
  } else {
    return [];
  }
}

function getFloorplansRoutes(user: User, id: string, flags: DealerCompanyFlags | null) {
  // lhphcapital-kdm6 (Kevin, 2026-10-26): "If dealers have an ACTIVE Floor Plan, they can view
  // the Floor Plan tab." Legacy shows it on company.FloorplanON. Read-only: no funding, billing,
  // payoffs or vehicle edits, and no vehicle detail page (its tabs are edit forms).
  if (isDealerMember(user)) {
    if (!anyFlagOn(flags, ['FloorplanON']) || !id) {
      return null;
    }
    return {
      name: 'Floorplans',
      path: '/floorplans',
      'data-test': '/floorplans',
      routes: [
        { name: 'Dashboard', path: `/floorplans/${id}/dashboard` },
        { name: 'Pending Funding', path: `/floorplans/${id}/pending-funding` },
        { name: 'Active Vehicles', path: `/floorplans/${id}/active-vehicles` },
        { name: 'Paid Off Vehicles', path: `/floorplans/${id}/paid-off-vehicles` },
        { name: 'Funding Log', path: `/floorplans/${id}/funding-log` },
        { name: 'Billing/Payment Log', path: `/floorplans/${id}/billing-payment-log` },
      ],
    };
  }

  if (!id) {
    return {
      name: 'Floorplans',
      path: '/floorplans',
    };
  }

  return {
    name: 'Floorplans',
    path: '/floorplans',
    'data-test': '/floorplans',
    routes: [
      {
        name: 'Dashboard',
        path: `/floorplans/${id}/dashboard`,
      },
      {
        name: 'Pending Funding',
        path: `/floorplans/${id}/pending-funding`,
      },
      {
        name: 'Active Vehicles',
        path: `/floorplans/${id}/active-vehicles`,
      },
      // Executes floorplan payoffs: LHPH staff only (lhphcapital-evtq).
      ...(canRunStaffActions(user)
        ? [
            {
              name: 'Payoff Requests',
              path: `/floorplans/${id}/payoff-requests`,
            },
          ]
        : []),
      {
        name: 'Paid Off Vehicles',
        path: `/floorplans/${id}/paid-off-vehicles`,
      },
      {
        name: 'Funding Log',
        path: `/floorplans/${id}/funding-log`,
      },
      {
        name: 'Billing/Payment Log',
        path: `/floorplans/${id}/billing-payment-log`,
      },
      {
        name: 'Balance History',
        path: `/floorplans/${id}/balance-history`,
      },
      // Assigning an outside lender: LHPH staff only (lhphcapital-evtq).
      ...(canRunStaffActions(user)
        ? [
            {
              name: 'Assign Lender',
              path: `/floorplans/${id}/assign-lender`,
            },
          ]
        : []),
      {
        name: 'Vendors',
        path: `/floorplans/${id}/vendors`,
      },
    ],
  };
}

function getBorrowingBaseRoutes(user: User, id: string, flags: DealerCompanyFlags | null) {
  // lhphcapital-kdm6 (Kevin, 2026-10-26): "If a dealer has an ACTIVE Borrowing Base, they can
  // view the Borrowing Base tab." Legacy shows it on company.BorrowBase. Read-only: Billing is
  // a staff action, and Transactions creates DocuSign envelopes, so both stay staff-only.
  if (isDealerMember(user)) {
    if (!anyFlagOn(flags, ['BorrowBase']) || !id) {
      return null;
    }
    return {
      name: 'Borrowing Base',
      path: '/borrowing-base',
      'data-test': '/borrowing-base',
      routes: [
        { name: 'Dashboard', path: `/borrowing-base/${id}/dashboard` },
        { name: 'Current AR', path: `/borrowing-base/${id}/current-ar` },
        { name: 'Billing Logs', path: `/borrowing-base/${id}/billing-logs` },
        { name: 'DocuSign Docs', path: `/borrowing-base/${id}/docusign-docs` },
      ],
    };
  }

  if (!id) {
    return {
      name: 'Borrowing Base',
      path: '/borrowing-base',
    };
  }

  return {
    name: 'Borrowing Base',
    path: '/borrowing-base',
    'data-test': '/borrowing-base',
    routes: [
      {
        name: 'Dashboard',
        path: `/borrowing-base/${id}/dashboard`,
      },
      {
        name: 'Current AR',
        path: `/borrowing-base/${id}/current-ar`,
      },
      {
        name: 'Transactions',
        path: `/borrowing-base/${id}/transactions`,
      },
      {
        name: 'Billing',
        path: `/borrowing-base/${id}/billing`,
      },
      {
        name: 'Billing Logs',
        path: `/borrowing-base/${id}/billing-logs`,
      },
      {
        name: 'DocuSign Docs',
        path: `/borrowing-base/${id}/docusign-docs`,
      },
    ],
  };
}

function getReportsRoutes(user: User) {
  // Legacy's dealer Reports list: seven reports run for the dealer's own company
  // (lhphcapital-3xb8). The staff pages below stay staff-only, in the API and as routes.
  if (isDealerMember(user)) {
    return {
      name: 'Reports',
      path: '/reports',
      'data-test': '/reports',
      routes: [
        {
          name: 'Dealer Reports',
          path: '/reports/dealer-reports',
        },
      ],
    };
  }

  return {
    name: 'Reports',
    path: '/reports',
    'data-test': '/reports',
    routes: [
      {
        name: 'Floor Plans',
        path: `/reports/floor-plans`,
      },
      {
        name: 'Credit Lines',
        path: `/reports/credit-lines`,
      },
      {
        name: 'Email Reports',
        path: `/reports/email-reports`,
      },
      {
        name: 'All Reports',
        path: `/reports/all-reports`,
      },
    ],
  };
}

function getImportsRoutes(user: User, id: string, flags: DealerCompanyFlags | null) {
  if (isDealerMember(user)) {
    if (!id) {
      return {
        name: 'Imports',
        path: '/imports',
      };
    }

    // lhphcapital-kdm6: each dealer import on the company flag legacy's Imports_N_M reads for it.
    return {
      name: 'Imports',
      path: '/imports',
      routes: dealerImports(flags).map(i => ({
        name: i.name,
        path: `/imports/${id}/${i.subpage}`,
      })),
    };
  }

  if (!id) {
    return {
      name: 'Imports',
      path: '/imports',
    };
  }

  return {
    name: 'Imports',
    path: '/imports',
    'data-test': '/imports',
    routes: [
      {
        name: 'Unsold Inventory',
        path: `/imports/${id}/unsold-inventory`,
      },
      {
        name: 'AR Aged',
        path: `/imports/${id}/ar-aged`,
      },
      {
        name: 'AR Balance',
        path: `/imports/${id}/ar-balance`,
      },
      {
        name: 'Acc Depreciation',
        path: `/imports/${id}/acc-depreciation`,
      },
      {
        name: 'Wholesale Inventory',
        path: `/imports/${id}/wholesale-inventory`,
      },
      {
        name: 'Interest Balance',
        path: `/imports/${id}/interest-balance`,
      },
      {
        name: 'VIN to Stock# Xref',
        path: `/imports/${id}/vin-to-stock-xref`,
      },
      {
        name: 'Floorplan Request',
        path: `/imports/${id}/floorplan-request`,
      },
      {
        name: 'Loan Requests',
        path: `/imports/${id}/loan-requests`,
      },
      {
        name: 'Premier Loan Requests',
        path: `/imports/${id}/premier-loan-requests`,
      },
      {
        name: 'Indirect Loan Requests',
        path: `/imports/${id}/indirect-loan-requests`,
      },
      {
        name: 'BHPH Loan Requests',
        path: `/imports/${id}/bhph-loan-requests`,
      },
      {
        name: 'Refi/Swap/Premier Conversion',
        path: `/imports/${id}/refi-swap-premier-conversion`,
      },
      {
        name: 'Check for Duplicates',
        path: `/imports/${id}/check-for-duplicates`,
      },
      {
        name: 'Current Dealer AR',
        path: `/imports/${id}/current-dealer-ar`,
      },
    ],
  };
}

function getAdminRoutes(user: User) {
  // Check if user can access admin section
  if (!isOrgAdmin(user) && !canAccessUsersMenu(user) && !isLHPHMember(user)) {
    return null;
  }

  // Dealer Admin gets Release Notes only (no Power BI, no admin tools)
  // Must be checked BEFORE isOrgAdmin since DealerAdmin is included in isOrgAdmin
  // No Users: a Dealer Admin creates and manages no logins (lhphcapital-fdjb), and the list
  // it would open loads from /api/admin/users, which refuses every dealer login.
  if (isDealerMember(user) && canAccessUsersMenu(user)) {
    return {
      name: 'Admin',
      path: '/admin',
      'data-test': '/admin',
      routes: [
        {
          name: 'Release Notes',
          path: '/admin/release-notes',
        },
      ],
    };
  }

  // SuperAdmin and Admin get all admin menus
  if (isOrgAdmin(user)) {
    return {
      name: 'Admin',
      path: '/admin',
      'data-test': '/admin',
      routes: [
        {
          name: 'Dashboard',
          path: '/admin/dashboard',
        },
        {
          name: 'Users',
          path: '/admin/users',
        },
        {
          name: 'Daily KPIs',
          path: '/admin/daily-kpis',
          icon: PowerBIIcon,
        },
        {
          name: 'Loans to Leases',
          path: '/admin/loans-to-leases',
          icon: PowerBIIcon,
        },
        {
          name: 'AR Aging',
          path: '/admin/ar-aging',
          icon: PowerBIIcon,
        },

        {
          name: 'DCS API',
          path: '/admin/dcs-api',
        },

        {
          name: 'Geolocation',
          path: '/admin/geolocation',
        },
        {
          name: 'Video Management',
          path: '/admin/videos',
        },
        {
          name: 'Letter Templates',
          path: '/admin/settings/letter-templates',
        },
        {
          name: 'Email Templates',
          path: '/admin/settings/email-templates',
        },
        {
          name: 'Charts CMS',
          path: '/admin/charts',
        },
        {
          name: 'AI Assistants',
          path: '/admin/ai-assistants',
        },
        {
          name: 'Document Library',
          path: '/admin/doc-library',
        },
        {
          name: 'Buyer Names',
          path: '/admin/buyer-names',
        },
        {
          name: 'LP Est Billing Detail',
          path: '/admin/lp-est-billing-detail',
        },
        {
          name: 'LP Daily Log',
          path: '/admin/lp-daily-log',
        },
        // Auth Log moved onto the Users page + a Dashboard widget. Restore this
        // entry (and the /admin/login-log route in AppRoutes.tsx) to bring back
        // the standalone page.
        // {
        //   name: 'Login Log',
        //   path: '/admin/login-log',
        // },
        {
          name: 'Logs',
          path: '/admin/logs',
        },
        {
          name: 'Release Notes',
          path: '/admin/release-notes',
        },
        ...(isSuperAdmin(user)
          ? [
              {
                name: 'Bug Reports',
                path: '/admin/bug-reports',
              },
              {
                name: 'FTP Status',
                path: '/admin/ftp-status',
              },
              {
                name: 'Test File Generator',
                path: '/admin/test-file-generator',
              },
              {
                name: 'Database Info',
                path: '/admin/database-info',
              },
            ]
          : []),

        {
          name: 'DocuSign',
          path: '/admin/docusign',
        },
        {
          name: 'BlackBook',
          path: '/admin/blackbook',
        },
        {
          name: 'Report Templates',
          path: '/admin/report-templates',
        },
        {
          name: 'Imports Monitor',
          path: '/admin/imports-monitor',
        },

        {
          name: 'Reports Generator (Swagger)',
          path: '/admin/swagger',
          external: true,
        },
        {
          name: 'API Docs (Swagger)',
          path: '/admin/api-swagger',
          external: true,
        },
        // Settings section hidden for now - routes still exist in AppRoutes.tsx
        // {
        //   name: 'Settings',
        //   path: '/admin/settings',
        //   routes: [
        //     {
        //       name: 'Users LHPH Staff',
        //       path: '/admin/settings/staff-users',
        //     },
        //     {
        //       name: 'Signatures',
        //       path: '/admin/settings/signatures',
        //     },
        //     {
        //       name: 'Email Templates',
        //       path: '/admin/settings/email-templates',
        //     },
        //     {
        //       name: 'Robo Templates',
        //       path: '/admin/settings/robo-templates',
        //     },
        //     {
        //       name: 'Letter Templates',
        //       path: '/admin/settings/letter-templates',
        //     },
        //     {
        //       name: 'Text Templates',
        //       path: '/admin/settings/text-templates',
        //     },
        //   ],
        // },
      ],
    };
  }

  // LHPH Admin gets Users + Release Notes + Power BI
  if (isLHPHMember(user) && canAccessUsersMenu(user)) {
    return {
      name: 'Admin',
      path: '/admin',
      'data-test': '/admin',
      routes: [
        {
          name: 'Users',
          path: '/admin/users',
        },
        {
          name: 'Release Notes',
          path: '/admin/release-notes',
        },
        {
          name: 'Daily KPIs',
          path: '/admin/daily-kpis',
          icon: PowerBIIcon,
        },
        {
          name: 'Loans to Leases',
          path: '/admin/loans-to-leases',
          icon: PowerBIIcon,
        },
        {
          name: 'AR Aging',
          path: '/admin/ar-aging',
          icon: PowerBIIcon,
        },
      ],
    };
  }

  // LHPH User gets Power BI only
  if (isLHPHMember(user)) {
    return {
      name: 'Admin',
      path: '/admin',
      'data-test': '/admin',
      routes: [
        {
          name: 'Daily KPIs',
          path: '/admin/daily-kpis',
          icon: PowerBIIcon,
        },
        {
          name: 'Loans to Leases',
          path: '/admin/loans-to-leases',
          icon: PowerBIIcon,
        },
        {
          name: 'AR Aging',
          path: '/admin/ar-aging',
          icon: PowerBIIcon,
        },
      ],
    };
  }

  return null;
}

// @ts-ignore
export function generateRoutesConfig(
  id: string = ':id',
  location: Location,
  user: User,
  dealerFlags: DealerCompanyFlags | null = null
) {
  // console.log('@@@ id, location, user', id, location.pathname, user);
  // A Guest (auditor) login gets the legacy read-only site's menu and nothing else
  // (utils/guestScreens.ts, lhphcapital-7xvj).
  if (isGuest(user)) {
    return { name: 'Home', path: '/', routes: guestMenu(id === ':id' ? '' : id) };
  }
  const routes = [
    {
      name: 'Home',
      path: '/home',
      'data-test': '/home',
      routes: getHomeRoutes(user),
    },
    getDealersRoutes(user, id),
    getLoansRoutes(user, id),
    getFloorplansRoutes(user, id, dealerFlags),
    getBorrowingBaseRoutes(user, id, dealerFlags),
    getReportsRoutes(user),
    getImportsRoutes(user, id, dealerFlags),
    {
      name: 'Video Training',
      path: '/video-training',
    },
    // Jon, 2026-10-13: AI Chat is staff only. The API refuses a dealer (lhphcapital-6bbr).
    canRunStaffActions(user)
      ? {
          name: 'AI Chat',
          path: '/ai-chat',
        }
      : null,
    getAdminRoutes(user),
    {
      name: 'Logout',
      path: '/logout',
    },
  ].filter(i => i !== null);

  return {
    name: 'Home',
    path: '/',
    routes,
  };
}
